/**
 * Mana Locals Admin — authentication and session boundary schemas.
 *
 * CONTRACT SOURCE: the API repository's M03 session-projection register
 * (`src/domains/m03-identity/contracts/session-projection.schemas.ts`) and the
 * Better Auth sign-in route the provider mounts at `/api/v1/auth`.
 *
 * ---------------------------------------------------------------------------
 * TWO AXES, KEPT APART
 * ---------------------------------------------------------------------------
 * The projection reports the actor's authority on TWO independent axes:
 *
 *   - `permissions` — the TENANT-scoped effective permissions of the selected tenant,
 *     resolved from the membership role through the ratified grant matrix.
 *   - `platformPermissions` — the actor's live PLATFORM grants on this deployment,
 *     resolved from `platform_permission_grants` (ADR-0004 Amendment 1).
 *
 * They are different policy cores and they are not merged. A platform administrator
 * and a tenant administrator are different people with different powers, and a client
 * that flattened the two axes would offer a merchant screen to somebody who may only
 * read a registry, or hide the registry from somebody who may read it.
 *
 * ---------------------------------------------------------------------------
 * NOTHING HERE IS AUTHORIZATION
 * ---------------------------------------------------------------------------
 * Zod validates SHAPE. Every operation is re-authorized by the server on every call,
 * so a client that renders the wrong screen because it read this file wrong is a
 * display bug, never a security hole.
 */
import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from '@/api/schemas/common';

/**
 * A permission code as the server issues it.
 *
 * The vocabulary is the API's, not the admin's: `catalog.offer.read`,
 * `inventory.stock.read`, `tenant.read` and so on. The admin does not invent codes
 * and does not interpret them beyond "does this list contain it".
 */
export const PermissionCodeSchema = z.string().min(1).max(128);

/**
 * The session projection: `GET /api/v1/identity/session`.
 *
 * A SELF-READ. It reports what the caller may do in the selected tenant, plus the
 * platform grants they hold. The API's response is `{ data }` with NO `meta`
 * member — the projection carries no correlation envelope — so this schema is
 * strict about `data` and says nothing about siblings.
 */
export const SessionProjectionSchema = z.object({
  data: z
    .object({
      user: z
        .object({
          id: UuidSchema,
          displayName: z.string().min(1).max(200),
          /** Tenant-scoped effective permissions for the selected tenant. */
          permissions: z.array(PermissionCodeSchema),
          /** Platform-scoped grants held on this deployment. May be empty. */
          platformPermissions: z.array(PermissionCodeSchema),
          /**
           * True only for an ENROLLED platform operator.
           *
           * It is a UX distinction and authorizes nothing: the platform authority
           * that actually decides is `platformPermissions`.
           */
          isPlatformOperator: z.boolean(),
        })
        .strict(),
      session: z
        .object({
          expiresAt: IsoDateTimeSchema,
        })
        .strict(),
      tenant: z
        .object({
          id: UuidSchema,
          slug: z.string().min(1),
        })
        .strict(),
    })
    .strict(),
});

export type SessionProjection = z.infer<typeof SessionProjectionSchema>;

/**
 * The provider's sign-in answer.
 *
 * The provider returns `{ redirect, token, user }`. The token is the raw session
 * credential and the admin NEVER reads it — the provider sets a first-party
 * `HttpOnly` cookie and the browser sends it back on its own. The field is parsed
 * only because the provider's response shape is validated as a whole; it is
 * deliberately NOT extracted into any application state.
 */
export const SignInResponseSchema = z.object({
  redirect: z.boolean().optional(),
  token: z.string().nullable().optional(),
  user: z
    .object({
      id: z.string().min(1),
      email: z.string().min(1),
      name: z.string().min(1),
      emailVerified: z.boolean().optional(),
    })
    .passthrough(),
});

export type SignInResponse = z.infer<typeof SignInResponseSchema>;

/**
 * The provider's sign-out answer.
 *
 * The provider's sign-out body is empty. The schema accepts an empty object and
 * nothing else, so a body that suddenly carries something is a contract change
 * rather than a silent pass-through.
 */
export const SignOutResponseSchema = z.object({}).loose();

export type SignOutResponse = z.infer<typeof SignOutResponseSchema>;

/**
 * The provider's sign-in FAILURE.
 *
 * The provider answers a refused sign-in with `{ message, code }` — NOT with the
 * shared API error envelope, because it is the provider's own route rather than a
 * platform operation. Both shapes are accepted so the login form can name the
 * refusal (`EMAIL_NOT_VERIFIED`, `INVALID_EMAIL_OR_PASSWORD`) instead of saying
 * "something went wrong".
 */
export const SignInErrorSchema = z
  .object({
    message: z.string().min(1),
    code: z.string().min(1),
  })
  .loose();

export type SignInError = z.infer<typeof SignInErrorSchema>;
